/**
 * NEXT_STEPS — the single registry of "what can I do after this artifact".
 *
 * Before this file the same job existed under up to four different labels in
 * four unrelated places: the hardcoded chip row in `generate-image-view.tsx`,
 * `ICP_CHIPS` in `SuggestionChips.tsx`, `IMAGE/VIDEO_AI_SUGGESTIONS` in
 * `MediaStudioAISuggestionBlock.tsx`, and ~20 free-text labels coached into the
 * model by `system-prompt-static.ts` + `src/lib/ai-agent/skills/*`. "Change the
 * background" (a bare prefill string) and "Remix background" (a typed action
 * that actually runs `iterateInPlace`) were the same button with different
 * words and different quality of execution.
 *
 * One entry per JOB. Chat and MediaStudioDialog both render from here, so the
 * two surfaces can never drift apart again.
 *
 * Two execution paths exist because the two surfaces have different routers:
 *   - `chatPrompt` — chat only has `onSuggestionClick(text, autoSend)`, so
 *     every step MUST carry a sendable string. It is CLEAN HUMAN TEXT with no
 *     URL in it: the old row pasted a 130-character storage URL into the user's
 *     own message bubble, which reads as broken to a non-technical creator.
 *     The artifact is resolved SERVER-side instead: detectLastGeneratedImage
 *     (server/api/agent/index.post.ts) auto-attaches the immediately preceding
 *     turn's image when the client sent none — the same mechanism that makes a
 *     bare "make it brighter" work. Chips render on exactly that turn, so
 *     "this image" is unambiguous by construction. `bindsAsset` records which
 *     steps depend on that resolution.
 *   - `namedAction` — the dialog routes typed actions through `onChipAction`.
 *     Optional: a step with no `namedAction` falls back to `chatPrompt` there.
 */

export type NextStepNamedAction =
  | 'animate'
  | 'upscale'
  | 'orbit'
  | 'push-shopify'
  | 'extend'
  | 'caption'
  | 'remix-background'
  | 'recolor';

export interface NextStep {
  id: string;
  label: string;
  /** Clean human sentence sent to the agent. Never contains a URL — see `bindsAsset`. */
  chatPrompt: string;
  /** This step acts on the turn's artifact (resolved server-side, not inlined). */
  bindsAsset?: boolean;
  /** The reference is the SOURCE for a video (routes the agent to generate_video). */
  videoIntent?: boolean;
  /**
   * false = seed the composer instead of firing. Reserve `true` for retrieval /
   * navigation; anything that produces, changes, or spends should be reviewable
   * first (the rule `provide-answer.ts:96-102` already states for the model).
   */
  autoSend: boolean;
  /** Typed dialog route. Absent → the dialog falls back to `chatPrompt`. */
  namedAction?: NextStepNamedAction;
}

/**
 * Deliberately no `posts` kind. A posts turn already has exactly one action row
 * — CompletionCard's Review / Approve / Schedule strip — and that strip is
 * STATEFUL in a way a static registry cannot express (Schedule only appears
 * once everything is approved, `CompletionCard.tsx:199`). Adding chips there
 * would recreate the second row this registry exists to delete.
 */
export type ArtifactKind = 'image' | 'video' | 'strategy' | 'brand_guidelines' | 'document';

export const NEXT_STEPS: Record<ArtifactKind, NextStep[]> = {
  image: [
    {
      id: 'animate',
      label: 'Animate this',
      chatPrompt: 'Make a video from this image.',
      bindsAsset: true,
      videoIntent: true,
      autoSend: false,
      namedAction: 'animate',
    },
    {
      id: 'remix-background',
      label: 'Remix background',
      chatPrompt:
        'Replace only the background with a clean, on-brand scene — keep the subject, pose, and lighting identical.',
      bindsAsset: true,
      autoSend: false,
      namedAction: 'remix-background',
    },
    {
      id: 'recolor',
      label: 'Try a different color',
      chatPrompt: 'Generate this product in our brand colors — keep composition identical.',
      bindsAsset: true,
      autoSend: false,
      namedAction: 'recolor',
    },
    {
      id: 'lifestyle-staging',
      label: 'Try lifestyle staging',
      chatPrompt: 'Restage this product in a natural lifestyle scene with soft daylight.',
      bindsAsset: true,
      autoSend: false,
    },
    {
      id: 'variation',
      label: 'Try another version',
      chatPrompt: 'Generate a variation of this image.',
      bindsAsset: true,
      autoSend: false,
    },
    {
      id: 'write-caption',
      label: 'Write a caption',
      chatPrompt: 'Create a post using this image.',
      bindsAsset: true,
      autoSend: false,
      namedAction: 'caption',
    },
    {
      id: 'push-shopify',
      label: 'Push to Shopify',
      chatPrompt: 'Push this image to Shopify.',
      bindsAsset: true,
      autoSend: false,
      namedAction: 'push-shopify',
    },
  ],
  video: [
    {
      id: 'extend',
      label: 'Extend 4s',
      chatPrompt: 'Extend this video by 4 seconds with the same scene.',
      autoSend: false,
      namedAction: 'extend',
    },
    {
      id: 'orbit-camera',
      label: 'Orbit camera',
      chatPrompt: 'Regenerate this video with an orbiting camera move around the subject.',
      autoSend: false,
      namedAction: 'orbit',
    },
    {
      id: 'slow-motion',
      label: 'Slow motion',
      chatPrompt: 'Regenerate at 60fps slow-motion, same scene, smoother frame interpolation.',
      autoSend: false,
    },
    {
      id: 'golden-hour',
      label: 'Golden hour lighting',
      chatPrompt: 'Re-light the scene with warm golden-hour cinematography — same camera move.',
      autoSend: false,
    },
    {
      id: 'cinematic',
      label: 'More cinematic',
      chatPrompt: 'Make it more cinematic — anamorphic depth, richer color grade, tighter framing.',
      autoSend: false,
    },
    {
      id: 'write-caption',
      label: 'Write a caption',
      chatPrompt: 'Create a post using this video.',
      autoSend: false,
      namedAction: 'caption',
    },
  ],
  strategy: [
    // P1.8 — the SAFE explore option stays first so there is a non-destructive
    // way to engage with the plan before committing to generation. Pre-fix the
    // first chip auto-sent "Generate all posts for this plan now" — one click
    // and the user had 14 posts they never asked to materialize.
    {
      id: 'walk-through-schedule',
      label: 'Walk me through the schedule',
      chatPrompt:
        "Walk me through this plan's schedule and explain why each pillar / cadence was chosen",
      autoSend: false,
    },
    {
      id: 'generate-posts',
      label: 'Generate posts from this plan',
      chatPrompt: 'Generate all posts for this plan now',
      autoSend: true,
    },
    {
      id: 'refine-audience',
      label: 'Refine the audience',
      chatPrompt: 'Refine the audience and update the strategy accordingly',
      autoSend: false,
    },
    {
      id: 'adjust-pillars',
      label: 'Adjust the pillars',
      chatPrompt: 'Suggest 1-2 alternative content pillars for this plan',
      autoSend: false,
    },
    {
      id: 'save-as-project',
      label: 'Save as a project',
      chatPrompt: 'Assign this strategy to a new project so I can build on it',
      autoSend: true,
    },
  ],
  brand_guidelines: [
    {
      id: 'save-as-brand-kit',
      label: 'Save as my brand kit',
      chatPrompt: 'Save this as my default brand kit',
      autoSend: true,
    },
    {
      id: 'sample-post',
      label: 'Show me a sample post',
      chatPrompt: 'Generate a sample post applying these brand guidelines',
      autoSend: true,
    },
    {
      id: 'refine-voice',
      label: 'Refine the voice',
      chatPrompt: 'Refine the voice — make it warmer and less corporate',
      autoSend: false,
    },
  ],
  document: [
    {
      id: 'turn-into-posts',
      label: 'Turn into posts',
      chatPrompt: 'Turn this document into platform-native social posts',
      autoSend: true,
    },
    {
      id: 'tighten',
      label: 'Tighten + shorten',
      chatPrompt: 'Tighten the language and cut 20% of the length',
      autoSend: false,
    },
    {
      id: 'add-references',
      label: 'Add references',
      chatPrompt: 'Add references and citations to back up the key claims',
      autoSend: false,
    },
    /**
     * The hero image, as an OFFER rather than an automatic charge.
     *
     * `blog-writer` used to mandate `generate_image` on every blog — verified
     * in prod on session 0a45b7fd, where two consecutive blog requests each
     * fired an unrequested image and each failed. The skill's own fallback was
     * to emit "Add a hero image" as FREE TEXT, which is the thing this registry
     * exists to stop: the registry owns the wording and the wiring so a
     * follow-up cannot drift per turn.
     *
     * `autoSend: false` on purpose. This one spends credits, and a chip that
     * fires a metered generation on a single unconfirmed click is the same
     * mistake in a smaller place — it seeds the composer so the user can aim it
     * first (`provide_answer`'s own rule: false for anything that produces,
     * changes or destroys something the user should review).
     */
    {
      id: 'add-hero-image',
      label: 'Add a hero image',
      chatPrompt: 'Generate a 16:9 hero image for this piece',
      autoSend: false,
    },
  ],
};

/**
 * What renders when the model picks nothing. Three is the ceiling — a turn
 * footer is a next step, not a menu.
 */
export const DEFAULT_NEXT_STEP_IDS: Record<ArtifactKind, string[]> = {
  image: ['animate', 'remix-background', 'write-caption'],
  video: ['extend', 'orbit-camera', 'write-caption'],
  strategy: ['walk-through-schedule', 'generate-posts', 'adjust-pillars'],
  brand_guidelines: ['save-as-brand-kit', 'sample-post', 'refine-voice'],
  document: ['turn-into-posts', 'tighten', 'add-references'],
};

const TOOL_TYPE_TO_KIND: Record<string, ArtifactKind> = {
  'tool-generate_image': 'image',
  'tool-generate_image_grid': 'image',
  'tool-create_image_variation': 'image',
  'tool-generate_video': 'video',
  'tool-generate_plan_strategy': 'strategy',
  'tool-generate_brand_guidelines': 'brand_guidelines',
  'tool-generate_document': 'document',
};

export function artifactKindForToolType(toolType: string): ArtifactKind | null {
  return TOOL_TYPE_TO_KIND[toolType] ?? null;
}

export function getNextStep(kind: ArtifactKind, id: string): NextStep | null {
  return NEXT_STEPS[kind].find((s) => s.id === id) ?? null;
}

/**
 * Resolve model-selected IDs to registry entries. Unknown IDs are DROPPED, not
 * rendered — a hallucinated id must never reach the user as a broken chip.
 * Empty / all-unknown falls back to the kind's default set so a successful
 * generation can never dead-end without a next step.
 */
export function resolveNextSteps(kind: ArtifactKind, ids: string[] | undefined): NextStep[] {
  const lookup = (list: string[]): NextStep[] =>
    list.map((id) => getNextStep(kind, id)).filter((s): s is NextStep => s !== null);

  const requested = lookup(ids ?? []);
  const resolved = requested.length > 0 ? requested : lookup(DEFAULT_NEXT_STEP_IDS[kind]);

  const seen = new Set<string>();
  const deduped: NextStep[] = [];
  for (const step of resolved) {
    if (seen.has(step.id)) continue;
    seen.add(step.id);
    deduped.push(step);
  }
  return deduped.slice(0, 3);
}

/** The user-visible sentence. The artifact rides along separately — see `bindsAsset`. */
export function resolveChatPrompt(step: NextStep): string {
  return step.chatPrompt;
}

/** Does this step need the turn's artifact attached to make sense? */
export function stepNeedsAsset(step: NextStep): boolean {
  return step.bindsAsset === true;
}

/** Every valid id across all kinds — used to document the enum for the model. */
export const ALL_NEXT_STEP_IDS: string[] = Array.from(
  new Set(Object.values(NEXT_STEPS).flatMap((steps) => steps.map((s) => s.id))),
);
